import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import http from "../../Utils/axiosConfig";
import ToastService from "../../Utils/ToastService";
import "../Analytics/Analytics.css";
import "./Bonus.css";

const fmt = (v) => (v != null ? Number(v).toFixed(2) : "0.00");
const TYPE_LABEL = { cow: "गाय", buffalo: "म्हैस" };

// Same hidden-iframe print helper used by DailyRegister.js / BillDetails.js —
// mobile browsers block sized popup windows, so printing goes through an iframe instead.
const printHtmlViaIframe = (html) => {
  let printFrame = document.getElementById("receipt-print-frame");
  if (!printFrame) {
    printFrame = document.createElement("iframe");
    printFrame.id = "receipt-print-frame";
    printFrame.style.position = "fixed";
    printFrame.style.right = "0";
    printFrame.style.bottom = "0";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "0";
    document.body.appendChild(printFrame);
  }

  const frameDoc = printFrame.contentWindow.document;
  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  printFrame.onload = () => {
    printFrame.contentWindow.focus();
    printFrame.contentWindow.print();
  };
};

// Indian financial year: 1 Apr – 31 Mar. Options run from FY 2020-21 through the current FY.
const buildFyOptions = () => {
  const today = new Date();
  const currentFyStart = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const options = [];
  for (let y = currentFyStart; y >= 2020; y--) {
    options.push({
      value: y,
      label: `${y}-${String(y + 1).slice(2)}`,
      from_date: `${y}-04-01`,
      to_date: `${y + 1}-03-31`,
    });
  }
  return options;
};

const Bonus = () => {
  const fyOptions = useMemo(() => buildFyOptions(), []);
  const dairyName = localStorage.getItem("dairy_name");

  const [selectedFy, setSelectedFy] = useState(fyOptions[0]?.value);
  const [basis, setBasis] = useState("milk");
  const [cowMultiplier, setCowMultiplier] = useState("");
  const [buffaloMultiplier, setBuffaloMultiplier] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const currentFy = fyOptions.find((f) => f.value === selectedFy) || fyOptions[0];

  const fetchData = async () => {
    if (!currentFy) return;
    setLoading(true);
    try {
      const res = await http.post("bonus/yearly-summary", {
        from_date: currentFy.from_date,
        to_date: currentFy.to_date,
      });
      setRows(res?.code === "DATA_FOUND" ? res.data : []);
    } catch (e) {
      console.error(e);
      ToastService.error("डेटा मिळवण्यात अडचण आली.");
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const cowMult = Number(cowMultiplier) || 0;
  const buffaloMult = Number(buffaloMultiplier) || 0;
  const computedRows = rows.map((r) => {
    const basisValue = basis === "rate" ? Number(r.total_rate) : Number(r.total_milk);
    const mult = r.type === "cow" ? cowMult : buffaloMult;
    return { ...r, bonus: basisValue * mult };
  });
  const totalBonus = computedRows.reduce((sum, r) => sum + r.bonus, 0);

  const basisLabel = basis === "rate" ? "रक्कम (₹)" : "दूध (लि.)";

  const buildReportHtmlRows = () =>
    computedRows
      .map(
        (r, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${r.dairy_id ?? ""}</td>
        <td style="text-align:left">${r.mr_name || r.en_name || ""}</td>
        <td>${TYPE_LABEL[r.type] || r.type || ""}</td>
        <td>${fmt(r.total_milk)}</td>
        <td>${fmt(r.total_rate)}</td>
        <td>${fmt(r.bonus)}</td>
      </tr>`,
      )
      .join("");

  const handlePrint = () => {
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
      <style>
        @page { margin:15mm; size:A4; }
        body{ font-family:Arial,sans-serif; font-size:12px; color:#000; }
        h2{ text-align:center; font-size:18px; margin-bottom:4px; }
        .sub{ text-align:center; font-size:13px; margin-bottom:16px; }
        table{ width:100%; border-collapse:collapse; margin-bottom:8px; }
        th{ background:#003366; color:white; padding:8px 6px; text-align:center; font-size:12px; }
        td{ padding:7px 6px; text-align:center; border-bottom:1px solid #ddd; font-size:12px; }
        tr:nth-child(even) td{ background:#f5f8ff; }
        .total-row td{ font-weight:bold; background:#eef2ff !important; }
        .footer{ text-align:center; margin-top:20px; font-size:11px; color:#666; }
      </style></head><body>
      <h2>${dairyName}</h2>
      <div class="sub">वार्षिक डिव्हिडंड अहवाल — ${currentFy?.label} | आधार: ${basisLabel} | गाय गुणक: ${cowMult} | म्हैस गुणक: ${buffaloMult}</div>
      <table>
        <thead><tr><th>क्र.</th><th>सभासद क्र.</th><th>नाव</th><th>प्रकार</th><th>दूध (लि.)</th><th>रक्कम (₹)</th><th>डिव्हिडंड (₹)</th></tr></thead>
        <tbody>
          ${buildReportHtmlRows()}
          <tr class="total-row"><td colspan="6">एकूण डिव्हिडंड</td><td>${fmt(totalBonus)}</td></tr>
        </tbody>
      </table>
      <div class="footer">Generated on ${new Date().toLocaleDateString("en-IN")}</div>
      </body></html>`;

    printHtmlViaIframe(html);
  };

  const handleExcel = () => {
    const sheetRows = computedRows.map((r, i) => ({
      "क्र.": i + 1,
      "सभासद क्र.": r.dairy_id ?? "",
      नाव: r.mr_name || r.en_name || "",
      प्रकार: TYPE_LABEL[r.type] || r.type || "",
      "दूध (लि.)": Number(fmt(r.total_milk)),
      "रक्कम (₹)": Number(fmt(r.total_rate)),
      "डिव्हिडंड (₹)": Number(fmt(r.bonus)),
    }));
    sheetRows.push({
      "क्र.": "",
      "सभासद क्र.": "",
      नाव: "एकूण",
      प्रकार: "",
      "दूध (लि.)": "",
      "रक्कम (₹)": "",
      "डिव्हिडंड (₹)": Number(fmt(totalBonus)),
    });

    const ws = XLSX.utils.json_to_sheet(sheetRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "डिव्हिडंड");
    XLSX.writeFile(wb, `labhansh-${currentFy?.label}.xlsx`);
  };

  const fetchPdfBlob = async () => {
    const res = await http.post(
      "bonus/yearly-summary-pdf",
      {
        from_date: currentFy?.from_date,
        to_date: currentFy?.to_date,
        basis,
        cow_multiplier: cowMult,
        buffalo_multiplier: buffaloMult,
        fy: currentFy?.label,
      },
      { responseType: "blob" },
    );
    return res instanceof Blob ? res : new Blob([res], { type: "application/pdf" });
  };

  const handlePdfDownload = async () => {
    try {
      const blob = await fetchPdfBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `labhansh-${currentFy?.label}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      ToastService.error("PDF तयार करण्यात अडचण आली.");
    }
  };

  const handleWhatsAppShare = async () => {
    try {
      const blob = await fetchPdfBlob();
      const file = new File([blob], `labhansh-${currentFy?.label}.pdf`, { type: "application/pdf" });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          text: `${dairyName} — वार्षिक डिव्हिडंड अहवाल (${currentFy?.label})`,
        });
      } else {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        ToastService.info("शेअरिंग सुविधा उपलब्ध नाही — PDF डाउनलोड झाली आहे, ती व्हॉट्सॲपवर मॅन्युअली पाठवा.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error(e);
        ToastService.error("शेअर करण्यात अडचण आली.");
      }
    }
  };

  return (
    <div className="an-container">
      <div className="an-filter-bar">
        <div className="an-filter-group">
          <label>आर्थिक वर्ष</label>
          <select
            className="bn-select"
            value={selectedFy}
            onChange={(e) => setSelectedFy(Number(e.target.value))}
          >
            {fyOptions.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        <div className="an-filter-group">
          <label>आधार</label>
          <div className="bn-basis-toggle">
            <button
              type="button"
              className={`bn-basis-btn ${basis === "milk" ? "bn-basis-btn--active" : ""}`}
              onClick={() => setBasis("milk")}
            >
              दूध
            </button>
            <button
              type="button"
              className={`bn-basis-btn ${basis === "rate" ? "bn-basis-btn--active" : ""}`}
              onClick={() => setBasis("rate")}
            >
              रक्कम
            </button>
          </div>
        </div>

        <div className="an-filter-group">
          <label>गाय गुणक 🐄</label>
          <input
            type="number"
            className="bn-multiplier-input"
            value={cowMultiplier}
            onChange={(e) => setCowMultiplier(e.target.value)}
            placeholder="0"
          />
        </div>

        <div className="an-filter-group">
          <label>म्हैस गुणक 🐃</label>
          <input
            type="number"
            className="bn-multiplier-input"
            value={buffaloMultiplier}
            onChange={(e) => setBuffaloMultiplier(e.target.value)}
            placeholder="0"
          />
        </div>

        <button className="an-search-btn" onClick={fetchData} disabled={loading}>
          {loading ? "..." : "शोधा"}
        </button>
      </div>

      {searched && rows.length === 0 ? (
        <div className="an-no-data">
          <p>या आर्थिक वर्षासाठी नोंदी उपलब्ध नाहीत.</p>
        </div>
      ) : (
        <>
          <div className="bn-action-bar">
            <button className="bn-action-btn" onClick={handlePrint} disabled={rows.length === 0}>
              🖨️ प्रिंट
            </button>
            <button className="bn-action-btn" onClick={handleExcel} disabled={rows.length === 0}>
              📊 एक्सेल
            </button>
            <button className="bn-action-btn" onClick={handlePdfDownload} disabled={rows.length === 0}>
              📄 PDF
            </button>
            <button className="bn-action-btn bn-action-btn--whatsapp" onClick={handleWhatsAppShare} disabled={rows.length === 0}>
              💬 व्हॉट्सॲप
            </button>
          </div>

          <div className="an-panel">
            <h3 className="an-panel-title">वार्षिक डिव्हिडंड — {currentFy?.label}</h3>
            <div className="an-table-wrap">
              <table className="an-table">
                <thead>
                  <tr>
                    <th>क्र.</th>
                    <th>सभासद क्र.</th>
                    <th>नाव</th>
                    <th>प्रकार</th>
                    <th>दूध (लि.)</th>
                    <th>रक्कम (₹)</th>
                    <th>डिव्हिडंड (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {computedRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="an-no-data-cell">
                        {loading ? "लोड होत आहे..." : "आर्थिक वर्ष निवडून \"शोधा\" दाबा."}
                      </td>
                    </tr>
                  ) : (
                    computedRows.map((r, i) => (
                      <tr key={r.mem_id}>
                        <td>{i + 1}</td>
                        <td>{r.dairy_id}</td>
                        <td className="an-name">{r.mr_name || r.en_name}</td>
                        <td>{TYPE_LABEL[r.type] || r.type}</td>
                        <td>{fmt(r.total_milk)}</td>
                        <td>{fmt(r.total_rate)}</td>
                        <td className="an-green">{fmt(r.bonus)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {computedRows.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={6} style={{ textAlign: "right", fontWeight: 700 }}>एकूण डिव्हिडंड</td>
                      <td className="an-green" style={{ fontWeight: 700 }}>{fmt(totalBonus)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Bonus;
